import apiClient from './client';
import { FileDataSummary } from '../utils/fileDataSummary';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Document extends FileDataSummary {
  id: number;
  tenant_id: number;
  project_id: number;
  uploaded_by: string;
  original_filename: string;
  display_name: string;
  description: string | null;
  mime_type: string | null;
  category: string;
  tags: string[];
  current_version: number;
  processing_status: string;
  sharing_policy: string;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

export interface DocumentVersion {
  id: number;
  document_id: number;
  version_number: number;
  uploaded_by: string;
  storage_path: string;
  file_size_bytes: number;
  sha256_hash: string;
  scan_status: string;
  change_note: string | null;
  created_at: string;
}

export interface DocumentDetail extends Document {
  versions: DocumentVersion[];
}

export interface GlobalDocument extends Document {
  project_name: string;
}

export interface DocumentLink {
  id: number;
  document_id: number;
  entity_type: string;
  entity_id: number;
  link_type: string;
  created_by: string;
  created_at: string;
}

export interface AuditEntry {
  id: number;
  document_id: number;
  version_id: number | null;
  user_id: string;
  action: string;
  detail: Record<string, any> | null;
  ip_address: string | null;
  request_id: string | null;
  timestamp: string;
}

export interface VerifyResult {
  verified: boolean;
  stored_hash: string;
  computed_hash: string;
  match: boolean;
}

// ---------------------------------------------------------------------------
// API functions — all at /api/v1/
// ---------------------------------------------------------------------------

export const documentsApi = {
  // Document CRUD

  uploadDocument: async (
    projectId: number,
    file: File,
    description?: string,
    onProgress?: (progress: number) => void,
  ): Promise<Document> => {
    const formData = new FormData();
    formData.append('file', file);
    if (description) formData.append('description', description);
    const response = await apiClient.post<Document>(
      `/v1/projects/${projectId}/documents`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && onProgress) {
            onProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          }
        },
      },
    );
    return response.data;
  },

  listDocuments: async (
    projectId: number,
    params?: { category?: string; search?: string; limit?: number; offset?: number },
  ): Promise<Document[]> => {
    const query = new URLSearchParams();
    if (params?.category) query.set('category', params.category);
    if (params?.search) query.set('search', params.search);
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());
    const qs = query.toString();
    const url = `/v1/projects/${projectId}/documents${qs ? `?${qs}` : ''}`;
    const response = await apiClient.get<Document[]>(url);
    return response.data;
  },

  listAllDocuments: async (
    params?: { category?: string; project_id?: number; search?: string; limit?: number; offset?: number },
  ): Promise<GlobalDocument[]> => {
    const query = new URLSearchParams();
    if (params?.category) query.set('category', params.category);
    if (params?.project_id) query.set('project_id', params.project_id.toString());
    if (params?.search) query.set('search', params.search);
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());
    const qs = query.toString();
    const url = `/v1/documents/global${qs ? `?${qs}` : ''}`;
    const response = await apiClient.get<GlobalDocument[]>(url);
    return response.data;
  },

  getDocument: async (documentId: number): Promise<DocumentDetail> => {
    const response = await apiClient.get<DocumentDetail>(`/v1/documents/${documentId}`);
    return response.data;
  },

  updateDocument: async (
    documentId: number,
    updates: { display_name?: string; description?: string; tags?: string[]; category?: string; sharing_policy?: string },
  ): Promise<Document> => {
    const response = await apiClient.patch<Document>(`/v1/documents/${documentId}`, updates);
    return response.data;
  },

  archiveDocument: async (documentId: number): Promise<void> => {
    await apiClient.delete(`/v1/documents/${documentId}`);
  },

  restoreDocument: async (documentId: number): Promise<void> => {
    await apiClient.post(`/v1/documents/${documentId}/restore`);
  },

  // Versioning

  uploadVersion: async (
    documentId: number,
    file: File,
    changeNote?: string,
    onProgress?: (progress: number) => void,
  ): Promise<DocumentVersion> => {
    const formData = new FormData();
    formData.append('file', file);
    if (changeNote) formData.append('change_note', changeNote);
    const response = await apiClient.post<DocumentVersion>(
      `/v1/documents/${documentId}/versions`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && onProgress) {
            onProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          }
        },
      },
    );
    return response.data;
  },

  listVersions: async (documentId: number): Promise<DocumentVersion[]> => {
    const response = await apiClient.get<DocumentVersion[]>(`/v1/documents/${documentId}/versions`);
    return response.data;
  },

  downloadUrl: (documentId: number): string => {
    return `/api/v1/documents/${documentId}/download`;
  },

  downloadVersionUrl: (documentId: number, versionNumber: number): string => {
    return `/api/v1/documents/${documentId}/versions/${versionNumber}/download`;
  },

  // Cross-linking

  createLink: async (
    documentId: number,
    entityType: string,
    entityId: number,
    linkType: string,
  ): Promise<DocumentLink> => {
    const response = await apiClient.post<DocumentLink>(`/v1/documents/${documentId}/links`, {
      entity_type: entityType,
      entity_id: entityId,
      link_type: linkType,
    });
    return response.data;
  },

  listLinks: async (documentId: number): Promise<DocumentLink[]> => {
    const response = await apiClient.get<DocumentLink[]>(`/v1/documents/${documentId}/links`);
    return response.data;
  },

  removeLink: async (documentId: number, linkId: number): Promise<void> => {
    await apiClient.delete(`/v1/documents/${documentId}/links/${linkId}`);
  },

  // Audit & integrity

  getAuditTrail: async (documentId: number): Promise<AuditEntry[]> => {
    const response = await apiClient.get<AuditEntry[]>(`/v1/documents/${documentId}/audit`);
    return response.data;
  },

  verifyIntegrity: async (documentId: number): Promise<VerifyResult> => {
    const response = await apiClient.get<VerifyResult>(`/v1/documents/${documentId}/verify`);
    return response.data;
  },

  // Evidence pack

  evidencePackUrl: (documentId: number): string => {
    return `/api/v1/documents/${documentId}/evidence-pack`;
  },

  projectEvidencePackUrl: (projectId: number): string => {
    return `/api/v1/projects/${projectId}/evidence-pack`;
  },
};
